import AppContext from 'App/AppContext';
import { IPDefaultButton, IPIconButton, IPLabel, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, globalTextStylesError } from 'globalStyles';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateSupportTicket } from 'services/Api/hubspotService';
import { apiRequest } from 'services/Auth/authConfig';
import { isValidEmail } from 'utils/string';
import GenericModal from './GenericModal';

const MAX_FILES = 5;
const MAX_FILES_LENGTH = 20 * 1024 * 1024; // 20 MB
const cameraIcon = { iconName: 'Camera' };
const attachIcon = { iconName: 'Attach' };
const deleteIcon = { iconName: 'Delete' };

type DisplayMediaOptionsWithHints = DisplayMediaStreamOptions & {
  preferCurrentTab?: boolean;
  selfBrowserSurface?: 'include' | 'exclude';
  surfaceSwitching?: 'include' | 'exclude';
  monitorTypeSurfaces?: 'include' | 'exclude';
};

const displayMediaOptions: DisplayMediaOptionsWithHints = {
  preferCurrentTab: true,
  selfBrowserSurface: 'include',
  surfaceSwitching: 'exclude',
  monitorTypeSurfaces: 'exclude',
  audio: false,
  video: {
    displaySurface: 'browser',
  } as MediaTrackConstraints,
};

interface ISupportTicketModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  initialSubject?: string;
}

const waitForBrowserPaint = async (): Promise<void> => {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
};

const SupportTicketModal = (props: ISupportTicketModalProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState<string>(props.initialSubject ?? '');
  const [description, setDescription] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [hideModalForScreenshot, setHideModalForScreenshot] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      setSubject(props.initialSubject ?? '');
      setDescription('');
      setEmail(appContext.user?.email ?? '');
      setFiles([]);
      setHideModalForScreenshot(false);
    }
  }, [props.isOpen, props.initialSubject, appContext.user?.email]);

  useEffect(() => {
    const canSend = (): boolean => {
      return (
        subject.trim().length > 0 &&
        description.trim().length > 0 &&
        email.trim().length > 0 &&
        isValidEmail(email) &&
        getTotalSize(files) < MAX_FILES_LENGTH &&
        !isSending
      );
    };

    setIsFormValid(canSend());
  }, [subject, description, email, isSending, files]);

  const addFiles = (newFiles: File[]) => {
    setFiles((prev) => {
      const combined = [...prev, ...newFiles];

      return combined.slice(0, MAX_FILES);
    });
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onFileInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(ev.target.files ?? []);
    addFiles(selected);
    ev.target.value = '';
  };

  const onTakeScreenshot = async () => {
    let stream: MediaStream | undefined;

    try {
      setIsCapturing(true);

      // Hide the support ticket modal and its backdrop before capturing.
      // The form state is preserved because it lives in this component state.
      setHideModalForScreenshot(true);

      // Also, close the help panel if it's open to avoid it being captured in the screenshot
      appContext.setHelpPanel(false);

      // Give React and the browser enough time to repaint without the modal.
      await waitForBrowserPaint();

      stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await video.play();

      await new Promise<void>((resolve) => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          resolve();
        } else {
          video.onloadeddata = () => resolve();
        }
      });

      // Small delay to avoid capturing a blank/initial frame in some browsers.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 200);
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not create screenshot canvas.');
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (blob) {
        addFiles([new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })]);
      }
    } catch {
      // User cancelled, selected the wrong surface, or browser is not supported.
    } finally {
      stream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());

      // Show the modal again after the screenshot attempt.
      setHideModalForScreenshot(false);
      setIsCapturing(false);
    }
  };

  const onSend = async () => {
    try {
      setIsSending(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiCreateSupportTicket(
        { subject: subject.trim(), description: description.trim(), email: email.trim() },
        files,
        accessToken,
      );
      appContext.showNotification(t('translation:SupportTicket.SuccessMessage'));
      props.onDismiss();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsSending(false);
    }
  };

  const getTotalSize = (files: File[]): number => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen && !hideModalForScreenshot}
      onClose={props.onDismiss}
      onSave={onSend}
      isActionPending={isSending}
      saveDisabled={!isFormValid}
      saveButtonText={t('translation:SupportTicket.SendButton')}
      title={t('translation:SupportTicket.Title')}
      subTitle={t('translation:SupportTicket.SubTitle')}
      height={700}
      width={540}
      maxWidth={'min(90vw, 540px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
      isBlocking={false}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <ScrollableStackItem>
          <IPStack tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPTextField
                label={t('translation:SupportTicket.EmailLabel')}
                required
                value={email}
                maxLength={255}
                onChange={(_ev, newValue) => setEmail(newValue ?? '')}
                onGetErrorMessage={(value) => {
                  if (!value || value.trim().length === 0) {
                    return t('translation:SupportTicket.EmailRequiredError');
                  } else if (!isValidEmail(value)) {
                    return t('translation:SupportTicket.EmailInvalidError');
                  }
                }}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPTextField
                label={t('translation:SupportTicket.SubjectLabel')}
                required
                value={subject}
                maxLength={255}
                onChange={(_ev, newValue) => setSubject(newValue ?? '')}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPTextField
                label={t('translation:SupportTicket.DescriptionLabel')}
                required
                multiline
                rows={5}
                value={description}
                maxLength={2000}
                onChange={(_ev, newValue) => setDescription(newValue ?? '')}
                style={{
                  resize: 'vertical',
                  maxHeight: 200,
                }}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPStack>
                <IPStack.Item>
                  <IPLabel>{t('translation:SupportTicket.AttachmentsLabel')}</IPLabel>
                </IPStack.Item>
                <IPStack horizontal tokens={globalStackTokensGapSmall} styles={{ root: { marginTop: 4 } }}>
                  <IPStack.Item>
                    <IPDefaultButton
                      iconProps={attachIcon}
                      disabled={files.length >= MAX_FILES || isCapturing}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('translation:SupportTicket.AttachFilesButton')}
                    </IPDefaultButton>
                  </IPStack.Item>
                  <IPStack.Item>
                    <IPDefaultButton
                      iconProps={cameraIcon}
                      disabled={files.length >= MAX_FILES || isCapturing}
                      onClick={onTakeScreenshot}
                      text={t('translation:SupportTicket.ScreenshotButton')}
                    ></IPDefaultButton>
                  </IPStack.Item>
                </IPStack>
              </IPStack>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onFileInputChange} />
              {files.length > 0 && (
                <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { marginTop: 8 } }}>
                  {files.map((file, index) => (
                    <IPStack.Item key={index}>
                      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                        <IPStack.Item grow>
                          <IPText variant="small">{file.name}</IPText>
                        </IPStack.Item>
                        <IPStack.Item>
                          <IPIconButton iconProps={deleteIcon} onClick={() => removeFile(index)} />
                        </IPStack.Item>
                      </IPStack>
                    </IPStack.Item>
                  ))}
                </IPStack>
              )}
              {files.length >= MAX_FILES && (
                <IPStack.Item>
                  <IPText variant="small" styles={globalTextStylesError}>
                    {t('translation:SupportTicket.MaxFilesReached', { max: MAX_FILES })}
                  </IPText>
                </IPStack.Item>
              )}
              {getTotalSize(files) >= MAX_FILES_LENGTH && (
                <IPStack.Item>
                  <IPText variant="small" styles={globalTextStylesError}>
                    {t('translation:SupportTicket.MaxFilesSize')}
                  </IPText>
                </IPStack.Item>
              )}
            </IPStack.Item>
          </IPStack>
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default SupportTicketModal;
